"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Bell } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Link from "@/components/link";
import { useMarkNotificationRead, useMarkNotificationsSeen, useNotifications } from "@/lib/api/notification";
import { renderNotification } from "@/lib/notifications/render";
import type { components } from "@/lib/api.gen";
type Notification = components["schemas"]["Notification"];
export function NotificationBell() {
  const $ = _c(62);
  const t = useTranslations();
  const {
    data
  } = useNotifications();
  const markSeen = useMarkNotificationsSeen();
  const markRead = useMarkNotificationRead();
  const [selected, setSelected] = useState(null);
  let t0;
  if ($[0] !== selected || $[1] !== t) {
    t0 = selected ? renderNotification(t, selected) : null;
    $[0] = selected;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const selectedText = t0;
  let t1;
  if ($[3] !== data?.notifications) {
    t1 = data?.notifications ?? [];
    $[3] = data?.notifications;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const notifications = t1;
  const unseen = data?.unseen_count ?? 0;
  let t2;
  if ($[5] !== markSeen || $[6] !== unseen) {
    t2 = function handleOpenChange(open) {
      if (open && unseen > 0) {
        markSeen.mutate();
      }
    };
    $[5] = markSeen;
    $[6] = unseen;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleOpenChange = t2;
  let t3;
  if ($[8] !== markRead) {
    t3 = function handleClick(notification) {
      if (!notification.read) {
        markRead.mutate(notification.id);
      }
      setSelected(notification);
    };
    $[8] = markRead;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleClick = t3;
  let t4;
  if ($[10] !== t) {
    t4 = t("notifications.label");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Bell />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== unseen) {
    t6 = unseen > 0 && <Badge className="absolute -top-1 -right-1 h-4 min-w-4 justify-center rounded-full px-1 text-[10px] bg-destructive text-white" data-testid="notification-badge">{unseen > 9 ? "9+" : unseen}</Badge>;
    $[13] = unseen;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t6) {
    t7 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" size="icon" className="relative" aria-label={t4} data-testid="notification-bell">{t5}{t6}</Button></DropdownMenuTrigger>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("notifications.title");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className="px-3 py-2 border-b text-sm font-medium">{t8}</div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== handleClick || $[23] !== notifications || $[24] !== t) {
    t10 = notifications.length === 0 ? <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t("notifications.empty")}</div> : notifications.map(notification_0 => {
      const {
        title,
        body
      } = renderNotification(t, notification_0);
      return <button key={notification_0.id} type="button" data-testid="notification-item" onClick={() => handleClick(notification_0)} className="w-full border-b px-3 py-2 text-left last:border-b-0 hover:bg-accent"><div data-testid="notification-title" className={notification_0.read ? "text-sm font-normal" : "text-sm font-bold"}>{title}</div><div className="line-clamp-2 text-xs text-muted-foreground">{body}</div></button>;
    });
    $[22] = handleClick;
    $[23] = notifications;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <div className="max-h-96 overflow-y-auto" data-testid="dropdown-notification-list">{t10}</div>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t9) {
    t12 = <DropdownMenuContent align="end" className="w-80 p-0">{t9}{t11}</DropdownMenuContent>;
    $[28] = t11;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== handleOpenChange || $[32] !== t12 || $[33] !== t7) {
    t13 = <DropdownMenu onOpenChange={handleOpenChange}>{t7}{t12}</DropdownMenu>;
    $[31] = handleOpenChange;
    $[32] = t12;
    $[33] = t7;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const t14 = !!selected;
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = open_0 => {
      if (!open_0) {
        setSelected(null);
      }
    };
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = selectedText?.title;
  let t17;
  if ($[36] !== t16) {
    t17 = <DialogTitle>{t16}</DialogTitle>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = selectedText?.body;
  let t19;
  if ($[38] !== t18) {
    t19 = <DialogDescription>{t18}</DialogDescription>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t17 || $[41] !== t19) {
    t20 = <DialogHeader>{t17}{t19}</DialogHeader>;
    $[40] = t17;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== selected || $[44] !== t) {
    t21 = selected?.link && <Button asChild={true}><Link href={selected.link} onClick={() => setSelected(null)}>{t("notifications.open")}</Link></Button>;
    $[43] = selected;
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("notifications.close");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t22) {
    t23 = <DialogClose asChild={true}><Button variant="outline">{t22}</Button></DialogClose>;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t21 || $[51] !== t23) {
    t24 = <DialogFooter>{t21}{t23}</DialogFooter>;
    $[50] = t21;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t20 || $[54] !== t24) {
    t25 = <DialogContent data-testid="notification-dialog">{t20}{t24}</DialogContent>;
    $[53] = t20;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t14 || $[57] !== t25) {
    t26 = <Dialog open={t14} onOpenChange={t15}>{t25}</Dialog>;
    $[56] = t14;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t13 || $[60] !== t26) {
    t27 = <>{t13}{t26}</>;
    $[59] = t13;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
}